import React, { useState, useEffect, FC } from "react";

import { Translate } from "../translate";
import { Link } from "../link";
import { useTranslations } from "@montana/containers/translations";
import { formatString } from "@montana/helpers/string-format/string-format.helper";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";
import { Button, ButtonStyles } from "../button";
import { useSettings } from "@montana/containers/settings/settings.container";
import { CUSTOMER_MODE } from "@montana/constants/customer-mode";
import { SearchInput } from "./search-input.component";
import { useCombinedSearch, useSearchCategories } from "./search.hook";
import { ProductCard } from "../product-card";
import { ISearchResultItem } from "@montana/models/generated/cms.interface";

export interface ISearchProps {
    onClick: Function;
}

const debounceTime = 300;

export const Search: FC<ISearchProps> = React.memo(({ onClick }: ISearchProps): JSX.Element => {
    const { customerMode } = useSettings();
    const { links, mostPopularSearchWords, professionalMostPopularSearchWords } = useDynamicSettings();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
    const [expandResults, setExpandResults] = useState(false);
    const [filter, setFilter] = useState<'all' | 'content' | 'products'>('all');
    const translate = useTranslations();
    const { searchCategories } = useSearchCategories(debouncedSearchQuery);
    const { products, content, totalResults, isSearching, totalContent = 0, totalProducts = 0 } = useCombinedSearch(debouncedSearchQuery);
    const isProfessional = customerMode === CUSTOMER_MODE.PROFESSIONAL;
    const searchWords: string[] | undefined = isProfessional ? professionalMostPopularSearchWords : mostPopularSearchWords;

    useEffect(() => {
        const debounceTimeOut = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, debounceTime);

        return () => {
            clearTimeout(debounceTimeOut);
        }
    }, [searchQuery])

    useEffect(() => {
        setExpandResults(false)
    }, [filter])


    return (
        <div className="search">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            {!!searchCategories?.length && (<div className="search__categories">
                {searchCategories?.slice(0, 3)?.map(sc => (
                    <Link to={sc.link} key={sc.name} className="search__category-link">{`${translate(t => t.filterAndSearch.goToCategoryPrefix, 'Go to')} `}<span>{sc.name}</span></Link>
                ))}
            </div>)}
            <aside className="search__meta">
                {isSearching && translate(t => t.filterAndSearch.searching, 'Searching...')}
                {!isSearching && !!totalContent && !!totalProducts && (
                    <div className="search__filters">
                        <Button className="search__filter" style={filter === 'all' ? ButtonStyles.Tertiary : ButtonStyles.Transparent} onClick={() => setFilter('all')} >
                            {translate(t => t.filterAndSearch.searchSeeAll, 'See all')}
                        </Button>
                        <Button className="search__filter" style={filter === 'content' ? ButtonStyles.Tertiary : ButtonStyles.Transparent} onClick={() => setFilter('content')} >
                            {formatString(translate(t => t.filterAndSearch.searchSeeContent, '{0} content pages found'), content?.length || 0)}
                        </Button>
                        <Button className="search__filter" style={filter === 'products' ? ButtonStyles.Tertiary : ButtonStyles.Transparent} onClick={() => setFilter('products')} >
                            {formatString(translate(t => t.filterAndSearch.searchSeeProducts, '{0} products found'), totalProducts)}
                        </Button>
                    </div>
                )}
                {!isSearching && totalProducts > 0 && (
                    <div className="search__result-info">
                        <Translate value={(t): string => t.filterAndSearch.productsFound}>
                            {(t): string => formatString(t, totalProducts)}
                        </Translate>
                        {isProfessional && !!links?.professionalSearchFilterPage && (
                            <>
                                <span className="search__meta-spacer">-</span>
                                <Link onClick={() => onClick()} to={`${links?.professionalSearchFilterPage}?q=${searchQuery}`} withStroke>
                                    <Translate value={(t): string => t.filterAndSearch.seeAll} />
                                </Link>
                            </>
                        )}
                        {!isProfessional && !!links?.searchFilterPage && (
                            <>
                                <span className="search__meta-spacer">-</span>
                                <Link onClick={() => onClick()} to={`${links?.searchFilterPage}?q=${searchQuery}`} withStroke>
                                    <Translate value={(t): string => t.filterAndSearch.seeAll} />
                                </Link>
                            </>
                        )}
                    </div>
                )}

                <div className="search__no-results">
                    {!isSearching && !!searchQuery && totalResults === 0 &&
                        <p>
                            <Translate value="filterAndSearch.couldNotFind" defaultValue="We couldn't find what you are looking for." />
                        </p>
                    }
                    {!isSearching && (!searchQuery || totalResults === 0) && (
                        <>
                            <h3>
                                <Translate value="filterAndSearch.popularSearches" defaultValue="Popular searches" />
                            </h3>
                            <div className="search__popular-searches">
                                {searchWords?.map(ps => (
                                    <Button key={ps} style={ButtonStyles.Transparent} onClick={() => setSearchQuery(ps)} >
                                        {ps}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </aside>
            {!!products?.length && filter !== 'content' && (
                <section className="search__results">
                    {products?.slice(0, expandResults || filter === 'products' ? undefined : 6)?.map((searchItem: ISearchResultItem, index: number) => (
                        <ProductCard
                            key={`${searchItem.code}`}
                            title={searchItem.title}
                            // imageUrl={searchItem.productImageViewModel?.imageRenderingMethod === LondonDynamicsImageRendering ? searchItem.productImageViewModel?.londonDynamicsStaticImageUrl : searchItem.productImageViewModel?.cylindoImageUrl}
                            imageUrl={searchItem.productImageViewModel?.londonDynamicsStaticImageUrl}
                            customImage={searchItem.productImageViewModel?.useAssetImage ? searchItem.productImageViewModel?.assetImageViewModel : undefined}
                            dimensions={searchItem.dimensions}
                            price={searchItem.price}
                            link={{ url: searchItem.link, title: searchItem.title }}
                            gtmProduct={{
                                id: searchItem.code || '',
                                name: searchItem.title || '',
                                list: '',
                                position: index + 1,
                                price: searchItem.price?.priceWithDiscount?.amount || 0,
                            }}
                            priceOverride={!!searchItem.price?.beforePriceExcludingVat?.formattedWithCurrency ? (
                                <div className="product-card__information-prices">
                                    <span className="product-card__information-current-price">
                                        {formatString(translate(t => t.filterAndSearch.fromExclVAT, 'From {0} excl. VAT'), searchItem.price?.beforePriceExcludingVat?.formattedWithCurrency)}
                                    </span>
                                </div>
                            ) : undefined
                            }
                            sizes={[
                                '(min-width: 1680px) 300px',
                                '(min-width: 1367px) 220px',
                                '(min-width: 811px) 30vw',
                                '(min-width: 480px) 50vw',
                                '80vw'
                            ].join(', ')}
                        />
                    ))}
                </section>
            )}
            {(products?.length || 0) > 6 && !expandResults && filter === 'all' && (
                <div className="search__expand-product-results">
                    <Button style={ButtonStyles.Tertiary} onClick={() => setExpandResults(true)}>
                        <Translate value={(t): string => t.filterAndSearch.searchShowMoreProducts} defaultValue="[Show more products]" />
                    </Button>
                </div>
            )}
            {!!content?.length && filter !== 'products' && (
                <section className={`search__results${totalProducts > 0 && expandResults || totalProducts <= 6 ? ' search__results--second' : ''}`}>
                    {content?.map((searchItem: ISearchResultItem, index: number) => (
                        <ProductCard
                            key={`${searchItem.title}-${index}`}
                            customImage={searchItem.productImageViewModel?.assetImageViewModel || {
                                url: 'missing_image'
                            }}
                            overrideInformation={
                                <p className="product-card__information-heading">{searchItem.title}</p>
                            }
                            link={{ url: searchItem.link, title: searchItem.title }}
                            sizes={[
                                '(min-width: 1025px) 306px',
                                '(min-width: 811px) 30vw',
                                '(min-width: 480px) 50vw',
                                '80vw'
                            ].join(', ')}
                        />
                    ))}
                </section>
            )}
        </div>
    );
});
